/* ============================================================================
   WPI CRM — Entity Model (the contract)
   Faithful to WPI-CRM-Functional-Spec §3, §4, §8. Every view, store slice, and
   seed file conforms to these shapes. Account-centric rollup, relationship-aware;
   many-to-many is the norm; history is never destroyed; snapshots are time-series.
   ========================================================================== */

/* ---- ID conventions (prefixed strings; stable across seed + store) ----
   acct-*, trk-*, con-*, opp-*, fund-*, trn-*, prop-*, fp-*, ai-*, wo-*,
   prj-*, evt-*, kd-*, dd-*, act-*, tkt-*, file-*, snap-* */
export type ID = string

export type ISODate = string // 'YYYY-MM-DD'
export type ISODateTime = string // 'YYYY-MM-DDTHH:mm:ssZ'

/* ============================ ENUMS ============================ */

export type AccountType =
  | '3PM' | 'Investor' | 'JV' | 'Vendor' | 'Personal' | 'Broker' | 'Municipal/Government'
export type AccountStatus =
  | 'Suspect' | 'Prospect' | 'Client' | 'Investor' | 'Personal'
  | 'Relationship' | 'Competitor' | 'Vendor'

export type TrackType =
  | 'Equity/Fund Partnership' | 'Advisory/Capital Markets' | '3PM'
  | 'Acquisition Channel' | 'Development/Entitlement'

export type Stance = 'Champion' | 'Ally' | 'Neutral' | 'Skeptic' | 'Opponent'
export type InfluenceLevel = 'High' | 'Medium' | 'Low'

// Canonical contact Type set (per AM contact spec). Internal/Municipal retained
// for seeded WPI-team and government records but are not primary form options.
export type ContactType =
  | '3PM' | 'Investor' | 'JV' | 'Vendor' | 'Personal' | 'Broker' | 'WPI Employee' | 'Municipal'
// Relationship = general catch-all; Competitor/Vendor per Jason's 7/28 ask
// (a contact can be "competitor slash potential 3PM client" — e.g. North
// Palisade — so these sit alongside the pipeline statuses, not a new field).
export type ContactStatus =
  | 'Suspect' | 'Prospect' | 'Client' | 'Investor' | 'Personal'
  | 'Relationship' | 'Competitor' | 'Vendor'
export type Holiday = 'Christmas' | 'Chanukah' | 'Ramadan' | 'Diwali' | 'Other'

/** Form option lists — single source of truth shared by the Contact form + list. */
export const CONTACT_TYPE_OPTIONS: ContactType[] = ['3PM', 'Investor', 'JV', 'Vendor', 'Personal', 'Broker', 'WPI Employee']
export const CONTACT_STATUS_OPTIONS: ContactStatus[] = ['Suspect', 'Prospect', 'Client', 'Investor', 'Personal', 'Relationship', 'Competitor', 'Vendor']
export const ACCOUNT_STATUS_OPTIONS: AccountStatus[] = ['Suspect', 'Prospect', 'Client', 'Investor', 'Personal', 'Relationship', 'Competitor', 'Vendor']
export const ACCOUNT_TYPE_OPTIONS: AccountType[] = ['3PM', 'Investor', 'JV', 'Vendor', 'Personal', 'Broker', 'Municipal/Government']
export const HOLIDAY_OPTIONS: Holiday[] = ['Christmas', 'Chanukah', 'Ramadan', 'Diwali', 'Other']

export type OpportunityType = 'Equity Partner' | 'Acquisition' | 'Development' | '3PM' | 'JV'
export type SalesType = 'Suspect' | 'Lead' | 'Prospect' | 'Opportunity'
export type DealContactRole =
  | 'Primary Contact' | 'Decision Maker' | 'Champion' | 'Influencer'
  | 'Investor' | '3P Partner' | 'Owner-Seller' | 'Owner-Buyer' | 'RE Broker' | 'Debt/Equity'
export type CompetitivePosition =
  | 'Partner of Choice' | 'Competitive Plus' | 'Competitive' | 'Competitive Minus'
export type AcquisitionStatus =
  | 'Initial Underwriting' | 'LOI' | 'PSA' | 'Due Diligence'
export type EntitlementStatus =
  | 'Pre-App' | 'Application' | 'Staff Review' | 'Planning Commission' | 'Council' | 'Permits Issued'

export type RelationshipType = 'Owned' | 'Managed-3PM' | 'JV' | 'Development'
export type PropertyState =
  | 'Target' | 'Under Contract' | 'Owned' | 'Managed' | 'In Development' | 'Disposition' | 'Offboarded'
export type StabilizationSubState = 'Lease-Up' | 'Stabilized'
export type UnitType = 'Non-Climate' | 'Climate' | 'RV' | 'Wine' | 'Mini-Warehouse'
export type PropertyHealth = 'Healthy' | 'Watch' | 'At-Risk'

export type Priority = 'Critical' | 'High' | 'Medium' | 'Low'
export type ItemStatus = 'Open' | 'In Progress' | 'Blocked' | 'Closed'
export type WorkOrderStatus = 'Open' | 'Dispatched' | 'In Progress' | 'Completed' | 'Cancelled'
export type ProjectType =
  | 'Investor Reporting' | 'Stabilization' | 'Capex' | 'Entitlement' | 'Onboarding' | 'Other'
export type ProjectStatus = 'Not Started' | 'On Track' | 'At Risk' | 'Behind' | 'Complete'

export type ScheduledEventType =
  | 'Performance Review' | 'Sales Meeting' | 'Reporting Window' | 'Hearing' | 'Deadline' | 'Site Visit' | 'Internal'
export type KeyDateType =
  | 'Refi' | 'Loan Maturity' | 'Valuation' | 'Covenant' | 'Reporting Window' | 'Stabilization'
  | 'Contract Renewal' | 'Budget' | 'Insurance' | 'Hard Money' | 'DD Expiration'
  | 'Extension' | 'Hearing' | 'Permit Expiration'
export type KeyDateStatus = 'Upcoming' | 'Due Soon' | 'Overdue' | 'Met' | 'Waived'

export type ActivityType = 'Note' | 'Email' | 'Call' | 'Task' | 'Meeting' | 'Milestone' | 'System'

export type Module = 'AM' | 'BD/3PM' | 'Acquisitions' | 'Development' | 'PM'

/** Polymorphic reference to any entity (for activities, files, action items, etc.). */
export type EntityKind =
  | 'account' | 'track' | 'contact' | 'opportunity' | 'fund' | 'property'
  | 'financialPeriod' | 'actionItem' | 'workOrder' | 'project' | 'event' | 'keyDate' | 'ddReport'
export interface EntityRef { kind: EntityKind; id: ID; label?: string }

/* ============================ SUPPORTING ============================ */

export interface Social { linkedIn?: string; instagram?: string; facebook?: string; tiktok?: string; youtube?: string }

export interface Address {
  line1?: string; line2?: string; city?: string; state?: string; zip?: string; country?: string
  lat?: number; lng?: number
}

/** Time-series sample — never overwritten; appended. */
export interface Snapshot<T = number> { date: ISODate; value: T }

export interface FileRef {
  id: ID; name: string; kind: 'pdf' | 'xlsx' | 'docx' | 'pptx' | 'image' | 'other'
  source: 'SharePoint' | 'OneDrive' | 'Upload' | 'Generated'
  url?: string; sizeKb?: number; uploadedBy?: string; uploadedAt?: ISODate
  attachedTo: EntityRef
}

export interface Activity {
  id: ID; type: ActivityType; title: string; body?: string
  at: ISODateTime; actor?: string // WPI team member
  withContacts?: ID[] // contact ids
  attachedTo: EntityRef[] // rolls up the chain
  tags?: string[]
  direction?: 'inbound' | 'outbound'
}

/** Mirrored, read-only operational ticket (ServiceChannel). */
export interface Ticket {
  id: ID; externalId?: string; title: string; propertyId?: ID
  status: string; trade?: string; vendor?: string
  openedAt?: ISODate; closedAt?: ISODate; cost?: number
}

/* ============================ CORE ============================ */

export interface Account {
  id: ID
  name: string
  type: AccountType
  status: AccountStatus
  hqAddress?: Address
  phone?: string
  website?: string
  ticker?: string
  description?: string
  sourceReferredBy?: string
  social?: Social
  logoText?: string // monogram for avatar
  trackIds: ID[]
  primaryContactId?: ID // the day-to-day relationship owner (NOT necessarily the decision maker)
  /** Manually archived (relationship ended / left the portfolio). */
  archived?: boolean
  /** True when the row is owned by the SQL02 investor sync — its NAME is source-managed and any name edit reverts on the next run. */
  synced?: boolean
  // rollups (denormalized for fast list views; derived from properties/financials)
  metrics?: AccountMetrics
}

export interface AccountMetrics {
  propertyCount?: number
  ownedCount?: number
  managedCount?: number
  stabilizedCount?: number
  revenueT12?: number
  noiT12?: number
  openItems?: number
  health?: PropertyHealth
  lastReview?: ISODate
  nextReview?: ISODate
}

export interface RelationshipTrack {
  id: ID
  accountId: ID
  name: string
  type: TrackType
  module: Module
  stage?: string
  status?: string
  wpiTeam: TeamMember[] // m:n WPI side
  counterpartyContactIds: ID[] // m:n with role captured on membership
  primaryContactId?: ID // day-to-day relationship owner for THIS track (distinct from decision maker)
  competesWithTrackIds?: ID[]
  description?: string
  fundIds?: ID[]
  opportunityIds?: ID[]
  propertyIds?: ID[]
  lastReview?: ISODate
  nextReview?: ISODate
}

export interface TeamMember { name: string; role: string; email?: string }

/** Contact ↔ Track membership: a contact can sit in multiple tracks with a role/stance per track. */
export interface TrackMembership { trackId: ID; role: string; stance?: Stance }

export interface Contact {
  id: ID
  accountId?: ID // optional — a contact need not belong to an account
  firstName: string
  lastName: string
  title?: string
  department?: string // Business line
  officeAddress?: Address
  sameAsHQ?: boolean
  country?: string
  officePhone?: string
  cellPhone?: string
  workEmail?: string
  personalEmail?: string
  birthday?: ISODate
  holiday?: Holiday
  sourceReferredBy?: string
  hobby?: string
  description?: string
  type?: ContactType
  status?: ContactStatus
  stance?: Stance // municipal/stakeholder tracking
  influence?: InfluenceLevel
  almaMater?: string
  degree?: string
  married?: boolean
  spouse?: string
  children?: string[]
  g2?: string // next-generation / G2 family contact (family-office relationship mapping)
  investorSymposium?: boolean
  symposiumYears?: number[] // years this person appears on the symposium's FINAL attendee artifact (day-of / check-in / name-tag list) — backfilled from Donna's master workbook 2026-07-23; proxy for attendance, not a turnstile record
  symposiumInvitedYears?: number[] // years this person appears on that year's invite list / mailing tabs — backfilled from Donna's master workbook 2026-07-27
  shirtSize?: string // apparel size for event gifts (XS–XXL) — sizing lives on the person, not on any one year's invite
  shirtStyle?: 'M' | 'W' // men's / women's cut
  social?: Social
  trackMemberships: TrackMembership[]
  isWpi?: boolean // internal WPI team member contact
  /** Archived instead of deleted (Charles's rule, 2026-07-28): hidden from
   *  everyone except crm.manage holders — the SERVER strips archived
   *  contacts from the dataset for non-managers. Admins can unarchive. */
  archived?: boolean
}

export interface Opportunity {
  id: ID
  name: string
  type: OpportunityType
  description?: string
  trackId?: ID
  principalAccountId: ID
  sourceContactId?: ID // advisor sourcing a deal landing elsewhere
  sourceAccountId?: ID
  sourceReferredBy?: string
  primaryContactId?: ID
  primaryContactRole?: DealContactRole
  salesType: SalesType
  salesStage: string // type-specific
  dealSize?: number
  estCloseQuarter?: string
  estCloseDate?: ISODate
  fundId?: ID
  trancheId?: ID
  propertyIds?: ID[]
  contactRoles?: { contactId: ID; side: 'WPI' | 'Account'; role: DealContactRole }[]
  dealNotes?: string
  keyDateIds?: ID[]
  // 3PM
  competitivePosition?: CompetitivePosition
  topCompetitors?: string[]
  estOnboardDate?: ISODate
  feeTerms?: string
  // Acquisition
  broker?: string
  seller?: string
  buyer?: string
  initialBid?: number
  currentBid?: number
  competitiveBidPosition?: string
  acquisitionStatus?: AcquisitionStatus
  strategy?: string // Buy/Hold, Opportunistic
  criteria?: 'Core' | 'Core Plus' | 'Value Add' | 'Development'
  hardMoneyDate?: ISODate
  ddExpiration?: ISODate
  extensionDeadline?: ISODate
  contingenciesRemoved?: boolean
  // Development
  entitlementStatus?: EntitlementStatus
  stakeholderIds?: ID[] // contact ids w/ stance
  permittingObstacles?: string
  requiredStudies?: string[]
  hearingDates?: ISODate[]
  outreachNotes?: string
}

export interface Fund {
  id: ID
  name: string
  vintage?: string
  targetSize?: number
  committedCapital?: number
  investorAccountIds: ID[]
  strategy?: string
  trackId?: ID
  tranches?: Tranche[]
}
export interface Tranche { id: ID; name: string; size?: number; status?: string }

/* ============================ PROPERTY ============================ */

export interface UnitMixRow { size: string; type: UnitType; count: number; sqft: number; nonStandard?: boolean }

export interface PropertyFinancing {
  lender?: string; loanAmount?: number; rate?: number; maturity?: ISODate
  ltv?: number; dscr?: number; lastValuation?: ISODate; nextValuation?: ISODate
}

export interface DemographicRing { radiusMi: 1 | 3 | 5; population?: number; households?: number; medianIncome?: number }

export interface ExternalIds {
  siteId?: number; siteLinkDb?: 0 | 1; lcode?: string; yardiCode?: string
  storTrackStoreId?: number; storTrackMasterId?: number; birdEyeId?: number
  gbpId?: string; umbracoId?: number; callPotentialId?: number; serviceChannelId?: number
}

export interface Property {
  id: ID
  name: string
  propertyType?: string
  relationshipType: RelationshipType
  ownerAccountId?: ID
  managingEntity?: string // 'WPI' for owned; client + WPI for 3PM
  address?: Address
  brand?: string
  website?: string
  currentOwner?: string
  social?: Social
  // lifecycle
  state: PropertyState
  stabilization?: StabilizationSubState
  onboardedDate?: ISODate
  stabilizationTarget?: ISODate
  stabilizedDate?: ISODate
  acquisitionDate?: ISODate
  // physical
  nrsf?: number
  tsf?: number
  totalUnits?: number
  unitTypes?: UnitType[]
  unitMix?: UnitMixRow[]
  occupiedSqFt?: Snapshot[]
  occupancyPct?: Snapshot[]
  /** Daily physical occupancy, trailing ~30 days (property Overview chart). */
  occupancyDaily?: Snapshot[]
  occByUnitType?: { type: UnitType; occPct: number }[]
  // market
  demographics?: DemographicRing[]
  numCompetitors?: number
  numREITs?: number
  sqftPerCapita?: number
  // financial
  revenue?: Snapshot[]
  /** Monthly NOI (actual $) from the synced Yardi income statements — complete months only. */
  noi?: Snapshot[]
  managementFeePct?: number // 3PM
  financialPeriodIds?: ID[]
  financing?: PropertyFinancing
  // reputation
  googleRating?: Snapshot[]
  googleReviewCount?: Snapshot[]
  startingRating?: number
  startingReviews?: number
  // operations / diligence-sourced
  fms?: string
  accessControl?: string
  auctionPlatform?: string
  tenantInsurance?: string
  officeHours?: string
  accessHours?: string
  uniqueFeatures?: string[]
  openCapexItems?: string[]
  // ids
  externalIds: ExternalIds
  // funds / tracks
  fundId?: ID
  trancheId?: ID
  trackId?: ID
  // insights cache
  health?: PropertyHealth
  /** Manually archived (sold / left the portfolio). Hidden from default views. */
  archived?: boolean
}

/* ============================ FINANCIALS ============================ */

export interface PnlLine { key: string; label: string; actual: number; budget: number; group: PnlGroup }
export type PnlGroup =
  | 'revenue' | 'controllable' | 'uncontrollable' | 'noi' | 'below-noi' | 'capex'

export interface FinancialPeriod {
  id: ID
  propertyId: ID
  period: string // '2026-Q1' | '2026-03' | 'T12-2026-03'
  grain: 'month' | 'quarter' | 'T12'
  basis: 'accrual' | 'cash'
  lines: PnlLine[]
  // computed convenience (also derivable from lines)
  totalRevenueActual?: number
  totalRevenueBudget?: number
  noiActual?: number
  noiBudget?: number
  source?: string // 'Yardi GL' | uploaded file
}

/* ============================ OPERATIONAL ============================ */

export interface ActionItem {
  id: ID; title: string; description?: string
  attachedTo: EntityRef
  priority: Priority; status: ItemStatus
  /** Display name of the assignee (one accountable person). */
  owner?: string
  /** Durable assignee key — matching/notifications use this, not the name. */
  ownerEmail?: string
  /** Account-level tasks can reference the properties they concern: ONE
   *  commitment, visible on each linked property's Action Items tab.
   *  (Independent per-site work is fanned out to separate tasks instead.) */
  relatedPropertyIds?: ID[]
  dueDate?: ISODate; source?: string
  createdAt?: ISODate; closedAt?: ISODate
}

export interface WorkOrder {
  id: ID; number: string; title: string; propertyId: ID
  vendor?: string; trade?: string; status: WorkOrderStatus
  openedAt?: ISODate; estCost?: number; actualCost?: number
  source?: string // ServiceChannel
}

export interface Milestone { name: string; due?: ISODate; done?: boolean }
export interface Project {
  id: ID; name: string; type: ProjectType; status: ProjectStatus
  pctComplete: number; milestones?: Milestone[]; dueDate?: ISODate
  propertyIds?: ID[]; attachedTo?: EntityRef; owner?: string; wiseFlag?: boolean
}

export interface ScheduledEvent {
  id: ID; title: string; type: ScheduledEventType
  start: ISODateTime; end?: ISODateTime
  attendees?: string[]; attachedTo?: EntityRef
  relatedPackage?: string; location?: string
  stage?: string // for sales meetings
}

export interface KeyDate {
  id: ID; title: string; type: KeyDateType; date: ISODate
  status: KeyDateStatus; attachedTo: EntityRef; owner?: string
  leadTimeDays?: number
}

/* ============================ DILIGENCE ============================ */

export interface DDFinding {
  id: string; section: string; item: string; note?: string
  capexFlag?: boolean; capexEst?: number; photos?: number; severity?: 'info' | 'minor' | 'major'
}
export interface DueDiligenceReport {
  id: ID; propertyId: ID; opportunityId?: ID
  status: 'Draft' | 'In Review' | 'Finalized'
  startedBy?: string; finalizedBy?: string; date?: ISODate
  findings: DDFinding[]
  highlights?: string[]
}

/* ============================ INSIGHTS (WISE) ============================ */

export interface FunnelStage { name: string; value: number; benchmark?: number; trend?: number[]; diagnosis?: string }
export interface InsightDriver { label: string; detail: string; tone: 'ok' | 'warn' | 'alert' | 'neutral'; ref?: EntityRef }
export interface RecommendedAction { title: string; rationale?: string; suggestedOwner?: string; priority?: Priority }

export interface PropertyInsight {
  propertyId: ID
  verdict: PropertyHealth
  narrative: string
  funnel?: FunnelStage[]
  drivers: InsightDriver[]
  recommended: RecommendedAction[]
}

export interface AccountInsight {
  accountId: ID
  trackId?: ID
  verdict: PropertyHealth
  narrative: string
  openItems?: { total: number; overdue: number; avgAgeDays: number; oldest?: string }
  criticalDates?: { title: string; date: ISODate; urgency: KeyDateStatus; ref?: EntityRef }[]
  drivers: InsightDriver[]
  recommended: RecommendedAction[]
}

/* ============================ ROOT DATASET ============================ */

export interface Dataset {
  accounts: Account[]
  tracks: RelationshipTrack[]
  contacts: Contact[]
  opportunities: Opportunity[]
  funds: Fund[]
  properties: Property[]
  financialPeriods: FinancialPeriod[]
  actionItems: ActionItem[]
  workOrders: WorkOrder[]
  projects: Project[]
  events: ScheduledEvent[]
  keyDates: KeyDate[]
  ddReports: DueDiligenceReport[]
  activities: Activity[]
  tickets: Ticket[]
  files: FileRef[]
  propertyInsights: PropertyInsight[]
  accountInsights: AccountInsight[]
}
